// BEGIN FILE: src/constants/planetOverlayPredictions.ts
/**
 * Chart Overlay Predictions
 * 
 * Defines predictions when a family member's planet falls in the native's house or on their Moon.
 * Used by FamilyHouseRelationshipsWidget to show how family members' planetary positions
 * influence the native's life areas.
 * 
 * Structure:
 * - Each planet has predictions for houses 1-12
 * - Special cases (like Saturn on Moon) are handled separately
 * - Sentiment: positive (green), negative (red), neutral (gray), mixed (orange)
 */

export type PlanetName = 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn' | 'Rahu' | 'Ketu';

export interface OverlayPrediction {
  planet: PlanetName;
  house: number;
  title: string;
  prediction: string;
  sentiment: 'positive' | 'negative' | 'neutral' | 'mixed';
}

/**
 * Saturn Overlay Predictions
 * Theme: Discipline, responsibility, restriction, maturity, karma
 */
export const SATURN_PREDICTIONS: Partial<Record<number, OverlayPrediction>> = {
  1: {
    planet: 'Saturn',
    house: 1,
    title: "Saturn on Your Lagna (Ascendant)",
    prediction: "You feel older, more responsible, or restrained around them. Supportive long-term, emotionally heavy short-term. This person brings maturity and discipline to your life but may feel like a burden initially.",
    sentiment: 'mixed'
  },
  4: {
    planet: 'Saturn',
    house: 4,
    title: "Saturn on Your 4th House (Home & Mother)",
    prediction: "Home feels serious, cold, or obligation-driven. Property issues, delayed peace, responsibility for family burdens. This person's presence makes domestic life feel like a duty rather than comfort.",
    sentiment: 'negative'
  },
  7: {
    planet: 'Saturn',
    house: 7,
    title: "Saturn on Your 7th House (Marriage & Partnership)",
    prediction: "Relationship feels like a contract, not romance. Can indicate loyalty—but joy must be worked for. This person is committed but lacks spontaneous warmth. Love is shown through responsibility.",
    sentiment: 'mixed'
  },
};

/**
 * Special Case: Saturn on Native's Moon
 * Affects emotions, nurturing, and mental peace
 */
export const SATURN_ON_MOON_PREDICTION: OverlayPrediction = {
  planet: 'Saturn',
  house: 0, // Special marker for Moon
  title: "Saturn on Your Moon (Emotions)",
  prediction: "Emotional distance, silence, 'unspoken expectations.' Love exists, warmth struggles. Often shows parents who provided materially but not emotionally. You feel their care through actions, not words.",
  sentiment: 'negative'
};

/**
 * Sun Overlay Predictions
 * Theme: Authority, ego, vitality, father, government, identity
 */
export const SUN_PREDICTIONS: Partial<Record<number, OverlayPrediction>> = {
  1: {
    planet: 'Sun',
    house: 1,
    title: "Sun on Your Lagna (Ascendant)",
    prediction: "They dominate the space. You feel seen—but also overshadowed. Classic father → child or dominant elder influence. Their presence is powerful and sometimes makes you question your own identity.",
    sentiment: 'mixed'
  },
  10: {
    planet: 'Sun',
    house: 10,
    title: "Sun on Your 10th House (Career & Status)",
    prediction: "Strong career push—sometimes support, sometimes pressure to 'perform.' This person influences your public reputation and professional path. Their expectations shape your ambitions.",
    sentiment: 'mixed'
  },
};

/**
 * Special Case: Sun on Native's Moon
 */
const SUN_ON_MOON_PREDICTION: OverlayPrediction = {
  planet: 'Sun',
  house: 0,
  title: "Sun on Your Moon (Emotions)",
  prediction: "Your emotions orbit their approval. Praise lifts you; criticism crushes you. This person's opinion deeply affects your emotional state and self-worth. Seeking validation becomes a pattern.",
  sentiment: 'negative'
};

/**
 * Moon Overlay Predictions
 * Theme: Emotions, nurturing, mother, comfort, mind, bonding
 */
export const MOON_PREDICTIONS: Partial<Record<number, OverlayPrediction>> = {
  1: {
    planet: 'Moon',
    house: 1,
    title: "Moon on Your Lagna (Ascendant)",
    prediction: "Deep emotional attachment. You feel 'known' by them—sometimes too much. This person understands your moods intuitively but may also invade your emotional boundaries.",
    sentiment: 'mixed'
  },
  4: {
    planet: 'Moon',
    house: 4,
    title: "Moon on Your 4th House (Home & Mother)",
    prediction: "Comfort, nourishment, emotional safety. This person often feels like home. One of the most nurturing family overlays—creates a sense of belonging and peace.",
    sentiment: 'positive'
  },
  8: {
    planet: 'Moon',
    house: 8,
    title: "Moon on Your 8th House (Transformation & Secrets)",
    prediction: "Emotional entanglement, mood swings, guilt-based bonds. Love + emotional turbulence. Deep connection but requires constant emotional processing and can feel draining.",
    sentiment: 'negative'
  },
};

/**
 * Mars Overlay Predictions
 * Theme: Energy, aggression, courage, action, conflict, siblings
 */
export const MARS_PREDICTIONS: Partial<Record<number, OverlayPrediction>> = {
  1: {
    planet: 'Mars',
    house: 1,
    title: "Mars on Your Lagna (Ascendant)",
    prediction: "Constant stimulation—motivation or irritation. Can feel like competition inside the family. This person energizes you but also triggers your reactive side. Dynamic but exhausting.",
    sentiment: 'mixed'
  },
  4: {
    planet: 'Mars',
    house: 4,
    title: "Mars on Your 4th House (Home & Mother)",
    prediction: "Domestic conflict, property disputes, aggressive atmosphere at home. This person brings fire to your domestic space. Arguments over real estate or family matters are common.",
    sentiment: 'negative'
  },
  6: {
    planet: 'Mars',
    house: 6,
    title: "Mars on Your 6th House (Health & Conflicts)",
    prediction: "Constant problem-solving together—or constant fighting. Seen in sibling rivalries. This person either helps you overcome obstacles or becomes the obstacle itself.",
    sentiment: 'mixed'
  },
};

/**
 * Special Case: Mars on Native's Moon
 */
const MARS_ON_MOON_PREDICTION: OverlayPrediction = {
  planet: 'Mars',
  house: 0,
  title: "Mars on Your Moon (Emotions)",
  prediction: "Emotional arguments, impatience, reactive fights. Love exists but tempers flare fast. This person triggers your emotional defensiveness. Quick to anger, quick to forgive—but exhausting.",
  sentiment: 'negative'
};

/**
 * Mercury Overlay Predictions
 * Theme: Communication, intellect, analysis, business, siblings
 */
export const MERCURY_PREDICTIONS: Partial<Record<number, OverlayPrediction>> = {
  // To be added (not in user's request)
};

/**
 * Jupiter Overlay Predictions
 * Theme: Wisdom, expansion, children, dharma, fortune, protection
 */
export const JUPITER_PREDICTIONS: Partial<Record<number, OverlayPrediction>> = {
  1: {
    planet: 'Jupiter',
    house: 1,
    title: "Jupiter on Your Lagna (Ascendant)",
    prediction: "Mentor energy. You grow around them. Often grandparents, teachers, or wise elders. This person brings wisdom, optimism, and expansion to your life. Their presence is uplifting.",
    sentiment: 'positive'
  },
  4: {
    planet: 'Jupiter',
    house: 4,
    title: "Jupiter on Your 4th House (Home & Mother)",
    prediction: "Protection of home, property luck, family stability. This person brings blessings to your domestic life. Home feels safe and abundant when they're around.",
    sentiment: 'positive'
  },
  9: {
    planet: 'Jupiter',
    house: 9,
    title: "Jupiter on Your 9th House (Dharma & Fortune)",
    prediction: "Strong ancestral blessings, shared values, dharmic alignment. This person connects you to higher wisdom and family traditions. Spiritual mentor within the family.",
    sentiment: 'positive'
  },
};

/**
 * Special Case: Jupiter on Native's Moon
 */
const JUPITER_ON_MOON_PREDICTION: OverlayPrediction = {
  planet: 'Jupiter',
  house: 0,
  title: "Jupiter on Your Moon (Emotions)",
  prediction: "Emotional healing, optimism, forgiveness. Safe emotional container. This person provides emotional wisdom and makes you feel mentally peaceful. Often a source of comfort and hope.",
  sentiment: 'positive'
};

/**
 * Venus Overlay Predictions
 * Theme: Love, beauty, comfort, luxury, relationships, harmony
 */
export const VENUS_PREDICTIONS: Partial<Record<number, OverlayPrediction>> = {
  1: {
    planet: 'Venus',
    house: 1,
    title: "Venus on Your Lagna (Ascendant)",
    prediction: "Natural affection, ease, mutual liking. One of the most pleasant family overlays. This person brings grace and sweetness to your life. You feel appreciated and valued.",
    sentiment: 'positive'
  },
  4: {
    planet: 'Venus',
    house: 4,
    title: "Venus on Your 4th House (Home & Mother)",
    prediction: "Beautiful home energy, emotional warmth, family harmony. This person creates aesthetic beauty and emotional comfort in domestic space. Home feels like a sanctuary.",
    sentiment: 'positive'
  },
};

/**
 * Special Case: Venus on Native's Moon
 */
const VENUS_ON_MOON_PREDICTION: OverlayPrediction = {
  planet: 'Venus',
  house: 0,
  title: "Venus on Your Moon (Emotions)",
  prediction: "Emotional sweetness, comfort, reassurance. This person calms you. One of the most soothing overlays—brings gentleness and emotional beauty to the relationship.",
  sentiment: 'positive'
};

/**
 * Rahu Overlay Predictions
 * Theme: Obsession, foreign, unconventional, ambition, illusion, disturbance
 */
export const RAHU_PREDICTIONS: Partial<Record<number, OverlayPrediction>> = {
  1: {
    planet: 'Rahu',
    house: 1,
    title: "Rahu on Your Lagna (Ascendant)",
    prediction: "You feel changed around them—sometimes confused about who you are. Magnetic but unsettling. This person disrupts your sense of self in ways both fascinating and disturbing.",
    sentiment: 'mixed'
  },
  4: {
    planet: 'Rahu',
    house: 4,
    title: "Rahu on Your 4th House (Home & Mother)",
    prediction: "Frequent relocations, instability, lack of peace at home. Often seen in families with migration or fractured homes. This person brings unpredictability to domestic life.",
    sentiment: 'negative'
  },
  7: {
    planet: 'Rahu',
    house: 7,
    title: "Rahu on Your 7th House (Marriage & Partnership)",
    prediction: "Unconventional relationship dynamics. Attraction + dissatisfaction loop. This person fascinates you but never quite fulfills expectations. Karmic relationship patterns.",
    sentiment: 'mixed'
  },
};

/**
 * Special Case: Rahu on Native's Moon
 */
const RAHU_ON_MOON_PREDICTION: OverlayPrediction = {
  planet: 'Rahu',
  house: 0,
  title: "Rahu on Your Moon (Emotions)",
  prediction: "Emotional manipulation, anxiety, obsession, unpredictability. This is a draining overlay if unsupported. Creates mental fog and emotional dependency. Requires strong boundaries.",
  sentiment: 'negative'
};

/**
 * Ketu Overlay Predictions
 * Theme: Detachment, spirituality, past life, loss, moksha, withdrawal
 */
export const KETU_PREDICTIONS: Partial<Record<number, OverlayPrediction>> = {
  1: {
    planet: 'Ketu',
    house: 1,
    title: "Ketu on Your Lagna (Ascendant)",
    prediction: "You feel invisible or misunderstood around them. Bond exists, but emotional distance grows over time. This person doesn't quite 'see' you, leading to feelings of isolation.",
    sentiment: 'negative'
  },
  4: {
    planet: 'Ketu',
    house: 4,
    title: "Ketu on Your 4th House (Home & Mother)",
    prediction: "Emotional rootlessness. Home never quite feels like home. This person's presence creates a sense of not belonging. Spiritual connection but emotional detachment.",
    sentiment: 'negative'
  },
};

/**
 * Special Case: Ketu on Native's Moon
 */
const KETU_ON_MOON_PREDICTION: OverlayPrediction = {
  planet: 'Ketu',
  house: 0,
  title: "Ketu on Your Moon (Emotions)",
  prediction: "Emotional withdrawal, silence, numbness. 'We don't talk about feelings' families. This person creates emotional distance. Love may exist but is never expressed openly.",
  sentiment: 'negative'
};

/**
 * Master lookup: All planet predictions indexed by planet name
 */
export const PLANET_OVERLAY_PREDICTIONS: Record<PlanetName, Partial<Record<number, OverlayPrediction>>> = {
  Saturn: SATURN_PREDICTIONS,
  Sun: SUN_PREDICTIONS,
  Moon: MOON_PREDICTIONS,
  Mars: MARS_PREDICTIONS,
  Mercury: MERCURY_PREDICTIONS,
  Jupiter: JUPITER_PREDICTIONS,
  Venus: VENUS_PREDICTIONS,
  Rahu: RAHU_PREDICTIONS,
  Ketu: KETU_PREDICTIONS,
};

/**
 * Special predictions for planets on native's Moon (not a house)
 */
export const PLANET_ON_MOON_PREDICTIONS: Partial<Record<PlanetName, OverlayPrediction>> = {
  Saturn: SATURN_ON_MOON_PREDICTION,
  Sun: SUN_ON_MOON_PREDICTION,
  Moon: undefined, // Moon on Moon doesn't make sense (same planet)
  Mars: MARS_ON_MOON_PREDICTION,
  Mercury: undefined, // To be added if needed
  Jupiter: JUPITER_ON_MOON_PREDICTION,
  Venus: VENUS_ON_MOON_PREDICTION,
  Rahu: RAHU_ON_MOON_PREDICTION,
  Ketu: KETU_ON_MOON_PREDICTION,
};

// END FILE: src/constants/planetOverlayPredictions.ts
